import { fetcher } from '../../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export interface ChecklistItem {
  passed: boolean;
  checked_at: string;
}

export type ChecklistResults = Record<string, ChecklistItem>;

export type ClosureStatus = 'INITIATED' | 'CHECKLIST_PENDING' | 'CHECKLIST_PASSED' | 'CLOSED';

export interface SchemeClosureRecord {
  id: string;
  schemeId: string;
  status: ClosureStatus;
  checklistResults: ChecklistResults;
  finalCorpus: number | null;
  totalPrizePaid: number | null;
  totalCommissionPaid: number | null;
  totalWriteOff: number | null;
  netBalance: number | null;
  closureCertificateRef: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  closedAt: string | null;
  archivalRef: string | null;
  createdAt: string;
}

export const closureKeys = {
  all: ['schemes', 'closure'] as const,
  detail: (schemeId: string) => [...closureKeys.all, schemeId] as const,
};

export function useClosureStatus(schemeId: string) {
  return useQuery({
    queryKey: closureKeys.detail(schemeId),
    queryFn: async () => {
      const response = await fetcher.get<SchemeClosureRecord>(
        `/staff/v1/chit-groups/${schemeId}/closure`,
      );
      return response;
    },
    enabled: !!schemeId,
  });
}

export function useRunChecklist(schemeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetcher.post<SchemeClosureRecord>(`/staff/v1/chit-groups/${schemeId}/closure/run-checklist`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: closureKeys.detail(schemeId) });
    },
  });
}

export function useApproveClosure(schemeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetcher.post<SchemeClosureRecord>(`/staff/v1/chit-groups/${schemeId}/closure/approve`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: closureKeys.detail(schemeId) });
      qc.invalidateQueries({ queryKey: ['schemes', 'detail', schemeId] });
    },
  });
}

const CHECKLIST_LABELS: Record<string, string> = {
  C01: 'All auctions settled',
  C02: 'All prize payouts completed',
  C03: 'No pending subscription dues',
  C04: 'All ledger entries reconciled',
  C05: 'Bank statements verified',
  C06: 'Regulatory filings complete',
  C07: 'All subscriber accounts settled',
  C08: 'Foreman commission accounted',
  C09: 'Final approval received',
};

export function getChecklistLabel(code: string): string {
  return CHECKLIST_LABELS[code] || code;
}

export interface RegulatorySubmission {
  id: string;
  schemeId: string;
  filingType: 'CLOSURE_INTIMATION';
  status: 'PENDING' | 'SUBMITTED' | 'ACKNOWLEDGED' | 'REJECTED';
  submittedAt: string | null;
  acknowledgedAt: string | null;
  referenceNumber: string | null;
  notes: string | null;
}

export function useRegulatorySubmissions(schemeId: string) {
  return useQuery({
    queryKey: ['schemes', schemeId, 'regulatory-submissions'],
    queryFn: async () => {
      const response = await fetcher.get<{ data: RegulatorySubmission[] }>(`/staff/v1/chit-groups/${schemeId}/regulatory-submissions`);
      // It might return `{ data: ... }` or directly array, let's assume it's array based on controller unless standard wrapper is used
      return Array.isArray(response) ? response : (response as { data?: RegulatorySubmission[] }).data || [];
    },
    enabled: !!schemeId,
  });
}
